import { useEffect, useRef } from "react";

import { listJobs } from "@/api/jobs";
import { useAccessToken } from "@/auth/useAccessToken";
import { isActive } from "@/lib/jobStatusBadge";
import { useChatStore } from "@/runtime/chatStore";
import { useJobsStore } from "@/runtime/jobsStore";

const ACTIVE_POLL_MS = 5_000;
const IDLE_POLL_MS = 60_000;

export function useJobsPollingDriver(): void {
  const activeTenantId = useChatStore((s) => s.activeTenantId);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const currentLimit = useJobsStore((s) => s.currentLimit);
  const { acquireToken } = useAccessToken();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const setJobs = useJobsStore.getState().setJobs;
    const setLoading = useJobsStore.getState().setLoading;
    const setLastTenantId = useJobsStore.getState().setLastTenantId;

    setLastTenantId(activeTenantId);
    if (!activeTenantId) {
      setJobs([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    async function tick(): Promise<void> {
      if (cancelled) return;
      try {
        const token = await acquireToken();
        const res = await listJobs(activeTenantId as string, token, { limit: currentLimit });
        if (cancelled) return;
        setJobs(res.jobs ?? []);
      } catch (err) {
        console.warn("[ADR-091] failed to list jobs", err);
      }
      if (cancelled) return;
      setLoading(false);
      const latestJobs = useJobsStore.getState().jobs;
      const hasActive = latestJobs.some((j) => isActive(j.status)) || isStreaming;
      const delay = hasActive ? ACTIVE_POLL_MS : IDLE_POLL_MS;
      timerRef.current = setTimeout(tick, delay);
    }
    void tick();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeTenantId, isStreaming, currentLimit, acquireToken]);
}
